import React from 'react';
import { state, ui } from '../../../../../../../../../interfaces/interfaces';
import { useAppSelector } from "@store/hooks";
import config from "@src/utilities/config";
import { postCard, postCustomer } from "@utils/general";
import { CardRequest, CustomerRequest } from "@utils/squareWebSDK/interface";
import Version from "@utils/version";
import AddressUtils from "@utils/address";

const env = config.debug ? "TEST" : "LIVE"

const getIdempotencyKey = () => {
    //"00000000-0000-0000-0000-000000000000"
    const uuid = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
        var r = Math.random() * 16 | 0, v = c === "x" ? r : (r && 0x3 | 0x8);
        return v.toString(16);
    });
    return uuid;
}

const PAYMENT_PROVIDER = "SQUARE";
const COMPANY_ID = 5;

const getAddressLines = () => {
    const scope = "BookingPaymentAddress";
    const addressObj = AddressUtils.convertAddressFormFieldsToAddress({ scope, ui })?.addressObj;
    return {
        addressLine1: addressObj?.line1 || "",
        addressLine2: addressObj?.line2 || "",
        addressLine3: addressObj?.line3 || "",
        city: addressObj?.postTown || "",
        postcode: addressObj?.postcode || "",
        county: addressObj?.county || "",
    }
};

export const useSquare = () => {
    const cardBrand = useAppSelector(({ ui }) => ui.SquareCardBrand)
    const cardNumberValid = useAppSelector(({ state }) => state.SquareCardNumberValid)
    const bookingCardSecured = useAppSelector(({ ui }) => ui.BookingCardSecured)
    const cvvValid = useAppSelector(({ state }) => state.SquareCardCVVValid)
    const error = useAppSelector(({ ui }) => ui.SquareError)
    const expirationValid = useAppSelector(({ state }) => state.SquareExpirationValid)
    const focus = useAppSelector(({ ui }) => ui.SquareFocus)
    const paymentForm = useAppSelector(({ ui }) => ui.SquarePaymentForm)
    const cardData = useAppSelector(({ ui }) => ui.SquarePaymentData)
    const info = useAppSelector(({ ui }) => ui.SquareInfo)
    const card = useAppSelector(({ ui }) => ui.SquareCard)
    const formLoaded = useAppSelector(({ ui }) => ui.SquareFormLoaded)
    const processing = useAppSelector(({ ui }) => ui.SquareProcessing)
    const squareLoaded = useAppSelector(({ ui }) => ui.SquareLoaded)
    const squareResetEnabled = useAppSelector(({ ui }) => ui.SquareResetEnabled)
    const succeeded = useAppSelector(({ ui }) => ui.SquareSucceeded)
    const customerId = useAppSelector(({ ui }) => ui.SquareCustomerId)
    const nonce = useAppSelector(({ ui }) => ui.SquareNonce)
    const verification = useAppSelector(({ ui }) => ui.SquareVerification)
    const userChallenged = useAppSelector(({ ui }) => ui.SquareUserChallenged)

    React.useEffect(() => {
        if (customerId) {
            const { addressLine1: AddressLine1, addressLine2: AddressLine2, addressLine3: AddressLine3, city: City, postcode: Postcode, county: County } = getAddressLines();
            const body: CardRequest = {
                PaymentProvider: PAYMENT_PROVIDER,
                CompanyId: COMPANY_ID,
                CustomerId: customerId,
                CardVerificationToken: verification,
                CardNonceToken: nonce,
                NameOnCard: ui.BookingPaymentContactTitle + " " + ui.BookingPaymentContactFirstName + " " + ui.BookingPaymentContactLastName,
                AddressLine1,
                AddressLine2,
                AddressLine3,
                City,
                Postcode,
                Reference: `Job ${state.JobId}`,
                Source: Version.appName(),
                LinkId: state.JobId,
                LinkTableName: "Jobs",
                LinkFieldName: "JobId",
                IdempotencyKey: getIdempotencyKey(),
                ExpiryMonth: 0,
                ExpiryYear: 0,
                County,
                Country: 'GB',
                MetaData: {}
            }
            postCard({ body, url: "/Customers/SetCard", env })
        }
    }, [customerId, nonce, verification])

    React.useEffect(() => {
        if (succeeded) {
            const { addressLine1: AddressLine1, addressLine2: AddressLine2, addressLine3: AddressLine3, city: City, postcode: Postcode, county: County } = getAddressLines();
            const body: CustomerRequest = {
                PaymentProvider: PAYMENT_PROVIDER,
                CompanyId: COMPANY_ID,
                Title: ui.BookingPaymentContactTitle || "",
                Forename: ui.BookingPaymentContactFirstName,
                Surname: ui.BookingPaymentContactLastName,
                Email: ui.BookingPaymentContactEmail,
                Phone: ui.BookingPaymentContactLandline || ui.BookingPaymentContactMobile || "",
                AddressLine1,
                AddressLine2,
                AddressLine3,
                City,
                Postcode,
                Source: Version.appName(),
                LinkId: state.JobId,
                LinkTableName: "Jobs",
                LinkFieldName: "JobId",
                Country: 'GB',
                County,
                MetaData: {}
            }
            postCustomer({ body, url: "/Customers/SetCustomer", env })
        }
    }, [succeeded])

    return {
        // squareAPI, 
        squareLoaded, cardBrand,
        bookingCardSecured,
        cardNumberValid,
        cvvValid,
        error,
        expirationValid,
        focus,
        paymentForm,
        cardData,
        info,
        squareResetEnabled,
        card,
        formLoaded,
        processing,
        succeeded,
        nonce,
        verification,
        userChallenged,
        customerId
    }
}